import React, {useCallback, useEffect, useState} from 'react'
import {defineMessage, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
// eslint-disable-next-line no-restricted-imports
import {css} from '@emotion/css'
import styled from '@emotion/styled'
import {useOfferCheckContext} from 'components/Accommodation/Rooms/OfferCheckContext'
import {getIsOfferCheckLoadingOverlayVisible} from 'modules/alternativeOffers/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Loader} from '@vio/atlas/Loader'
import {Overlay} from '@vio/atlas/Overlay'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {noop} from '@vio/core/src/utils/function'

const Wrapper = styled.div`
  border-radius: ${cssTheme.layout.radius.xl};
  background: ${cssTheme.colors.background.neutral.primary};
  display: flex;
  width: 240px;
  padding: ${cssTheme.layout.spacing.s700} ${cssTheme.layout.spacing.s600};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s500};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const LoadingAnimationWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const overlayContainerStyles = css`
  background: unset !important;
  display: flex;
  align-items: center;
  justify-content: center;
`

const loadingMessages = [
  defineMessage({
    id: 'offerCheck.availability',
    defaultMessage: 'Checking availability...'
  }),
  defineMessage({
    id: 'offerCheck.confirming',
    defaultMessage: 'Confirming your deal...'
  }),
  defineMessage({
    id: 'dealFreeze.loadingAlmostThere',
    defaultMessage: 'Almost there...'
  }),
  defineMessage({
    id: 'dealFreeze.loadingStillWorkingOnIt',
    defaultMessage: 'Still working on it...'
  })
]

const INTERVAL_TIMEOUT = 8000

const LoadingIndicator = ({intervalTimeout}: {intervalTimeout: number}) => {
  const {formatMessage} = useIntl()
  const [currentMessage, setCurrentMessage] = useState(0)

  const next = useCallback(() => {
    setCurrentMessage(value => {
      const nextValue = value + 1
      // loop between last message and second last message
      return nextValue === loadingMessages.length ? nextValue - 2 : nextValue
    })
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      next()
    }, intervalTimeout)

    return () => {
      clearInterval(interval)
    }
  }, [next, intervalTimeout])

  return (
    <Wrapper>
      <LoadingAnimationWrapper dir="ltr">
        <Loader />
      </LoadingAnimationWrapper>
      <Box paddingBottom="s500">
        <Text variant="titleM" align="center">
          {formatMessage(loadingMessages[currentMessage])}
        </Text>
      </Box>
    </Wrapper>
  )
}

export const OfferCheckLoadingOverlay = ({
  intervalTimeout = INTERVAL_TIMEOUT
}: {
  intervalTimeout?: number
}) => {
  const isOpenFromRedux = useSelector(getIsOfferCheckLoadingOverlayVisible)
  const {isUserInitiatedCheckPending} = useOfferCheckContext()
  const isOpen = isOpenFromRedux || isUserInitiatedCheckPending
  return (
    <Overlay
      isOpen={isOpen}
      animationType={isOpen ? 'overlayFade' : 'none'}
      shadeBackground
      onShadeBackgroundClick={noop}
      customStyles={overlayContainerStyles}
      dataId="OfferCheckLoadingOverlay"
    >
      <LoadingIndicator intervalTimeout={intervalTimeout} />
    </Overlay>
  )
}
